import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Axios from 'axios';
import { Button } from '../styles';
import { Flex, Text, Loading } from '../components';
import Status, { FailedContainer, normalizeStatus } from './status';
import { PageTitle } from '../biz-components/page-title';

const Container = styled.div`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
`;

// Mirror of Ehx::RegistrationStatus::EMPLOYEE_FORM_COMPLETED, introduced by
// workstream-backend #10075 (pre-#10075 backend: Ehx::Employee::FINAL_STATUSES,
// a narrower list); keep in sync. 'Withdrawn' is deliberately absent until
// EHX confirms withdrawn applications cannot be reinstated - completing the
// step here is not reversible.
const EMPLOYEE_FORM_COMPLETED = [
  'Authorized by Signers',
  'Ready for Custodial Review',
  'Under Custodial Review',
  'Pending External Review',
  'Pending External Info',
  'Pending Client Info',
  'Approved by Custodian',
  'Declined External Review',
  'Not Eligible',
];

const FORM_COMPLETED_LOOKUP = new Set(
  EMPLOYEE_FORM_COMPLETED.map((status) => status.toLowerCase()),
);

export const isFormCompleted = (status) => FORM_COMPLETED_LOOKUP.has(normalizeStatus(status));

// Backend rejection when the action is already completed (e.g. the backend
// self-completed it during the fetch above).
const ACTION_ALREADY_FINISHED = 43002;

export const markAsComplete = ({ digestKey }) => {
  const apiURL = `${APIHOST}/v1/onboarding/employee/employee_onboarding_actions/${digestKey}/completed`;
  const config = {
    method: 'PUT',
    url: apiURL,
    headers: {
      ...(authCode && { Authorization: authCode }),
    },
  };
  return Axios(config).catch((error) => {
    const response = error && error.response;
    const errorCode = response && response.data && response.data.error_code;
    if (errorCode === ACTION_ALREADY_FINISHED) {
      return;
    }
    throw error;
  });
};

function EhxWotcModule(props) {
  const { processUUID, action, onEhxWotcSuccessed } = props;
  const [ssoLink, setSsoLink] = useState(null);
  const [ehxEmployee, setEhxEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchSSOLinkFailed, setFetchSSOLinkFailed] = useState(false);
  const [fetchEmployeeFailed, setFetchEmployeeFailed] = useState(false);
  const [markCompleteFailed, setMarkCompleteFailed] = useState(false);
  const [showEmployeeStatus, setShowEmployeeStatus] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  useEffect(() => {
    if (processUUID) {
      setIsLoading(true);
      Axios.get(
        `${APIHOST}/v1/onboarding/ehx_wotc/worker/employees/by_employee_onboarding_process?employee_onboarding_process_uuid=${processUUID}`,
      )
        .then((response) => {
          setEhxEmployee(response.data);
          const employeeId = response.data.id;
          if (employeeId) {
            Axios.post(`${APIHOST}/v1/onboarding/ehx_wotc/worker/employees/${employeeId}/sso_link`)
              .then((response) => {
                setSsoLink(response.data.sso_link);
              })
              .catch(() => {
                setIsLoading(false);
                setFetchSSOLinkFailed(true);
              });
          }
        })
        .catch(() => {
          setIsLoading(false);
          setFetchSSOLinkFailed(true);
        });
    }
  }, []);

  if (isLoading) {
    <Flex>
      <Loading className='spinner spinner-blue' />
    </Flex>;
  }

  if (fetchSSOLinkFailed) {
    return (
      <Flex>
        <Text>Something went wrong while rendering the WOTC form</Text>
      </Flex>
    );
  }
  const isOnboardingV2On = isOnboardingV2 === 'true';
  return (
    <Flex
      column
      style={{
        justifyContent: 'stretch',
      }}
    >
      {
        isOnboardingV2On && <PageTitle title={action.module_name} />
      }
      <Text
        style={{
          fontSize: '18px',
          fontWeight: 700,
          color: '#464D5D',
        }}
      >
        Fill WOTC form
      </Text>
      <Container>
        {ssoLink && (
          <iframe
            src={ssoLink}
            title='EHX form'
            onLoad={() => {
              setIsLoading(false);
            }}
            style={{
              border: 'none',
              height: '700px',
              display: 'block',
              width: '100%',
              minHeight: '530px',
            }}
          />
        )}
      </Container>
      <Flex
        style={{
          justifyContent: 'right',
          alignItems: 'center',
        }}
      >
        {showEmployeeStatus && ehxEmployee && <Status status={ehxEmployee.registration_status} />}
        {fetchEmployeeFailed && (
          <FailedContainer>
            <img src='/j/images/employee-onboarding/circle-exclamation.svg' />
            <Text
              style={{
                color: '#464D5D',
                fontSize: '14px',
                paddingLeft: '10px',
              }}
            >
              Something went wrong while fetching the employee status, please try again later.
            </Text>
          </FailedContainer>
        )}
        {markCompleteFailed && (
          <FailedContainer>
            <img src='/j/images/employee-onboarding/circle-exclamation.svg' alt='' />
            <Text
              style={{
                color: '#464D5D',
                fontSize: '14px',
                paddingLeft: '10px',
              }}
            >
              Your form was submitted, but we could not mark this step complete. Please try again.
            </Text>
          </FailedContainer>
        )}
        <Button
          style={{
            backgroundColor: '#3A67D4',
            color: '#FFFFFF',
            width: '120px',
            height: '48px',
            padding: '10px 16px',
            marginLeft: '20px',
            marginRight: '20px',
          }}
          data-testid='ehx_wotc_done_button'
          loading={isButtonLoading}
          disabled={!ehxEmployee || isButtonLoading}
          onClick={() => {
            if (!ehxEmployee) {
              return;
            }
            setIsButtonLoading(true);
            setShowEmployeeStatus(false);
            setFetchEmployeeFailed(false);
            setMarkCompleteFailed(false);
            const employeeId = ehxEmployee.id;
            Axios.post(
              `${APIHOST}/v1/onboarding/ehx_wotc/worker/employees/${employeeId}/fetch`
            ).then((response) => {
              const employeeData = response.data;
              setEhxEmployee(employeeData);

              if (isFormCompleted(employeeData.registration_status)) {
                markAsComplete({ digestKey: action.digest_key }).then(
                  () => {
                    setIsButtonLoading(false);
                    try {
                      onEhxWotcSuccessed();
                    } catch (navigationError) {
                      // The step is completed server-side; a fresh load
                      // advances past it instead of dead-ending silently.
                      console.error(navigationError);
                      window.location.reload();
                    }
                  },
                  (completionError) => {
                    console.error(completionError);
                    setMarkCompleteFailed(true);
                    setIsButtonLoading(false);
                  },
                );
              } else {
                setShowEmployeeStatus(true);
                setIsButtonLoading(false);
              }
            }).catch((fetchError) => {
              console.error(fetchError);
              setFetchEmployeeFailed(true);
              setIsButtonLoading(false);
            });
          }}
        >
          <Text
            style={{
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            Done
          </Text>
        </Button>
      </Flex>
    </Flex>
  );
}

export default EhxWotcModule;
